import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import WhatsAppFAB from "./WhatsAppFAB";
import {
  ArrowLeft, ArrowRight, Lock, Sliders, RotateCcw,
  Crown, AlertCircle, Plane, FileText, CreditCard, ChevronDown
} from "lucide-react";

const fmtUSD = (n, dec = 0) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(n);
const fmtNum = (n, dec = 0) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(n);

// ============== CONSTANTES ==============
const SEMANAS = 52;
const LEAD_COST = 4;            // $4 por lead
const LEADS_PER_CITA = 25;      // 25 leads para conseguir 1 cita
const VISA_PRICE = 49;          // $49 asesoría visa por persona
const PAQUETE_PRICE = 598;      // $598 paquete a Orlando (exit sale)
const CLUB_COST = 499;          // $499 costo del Club Vacacional al broker por venta
const FAMILIARES_EXTRA = 2;     // cada comprador de visa adiciona 2 familiares
const MOROSIDAD_PCT = 10;       // 10% de morosidad sobre las cuotas financiadas

// 4 niveles de cierre del Club Vacacional
const NIVELES = [
  { key: "amateur",    label: "Amateur",     pct: 10, color: "#94a3b8" },
  { key: "calentando", label: "Calentando",  pct: 20, color: "#1d4ed8" },
  { key: "pro",        label: "Pro",         pct: 30, color: "#c9a227" },
  { key: "elite",      label: "Elite",       pct: 40, color: "#80C753" },
];

const DEFAULTS = {
  vendedores: 1,
  citasDia: 2,
  diasSem: 6,
  nivel: "pro",          // Pro 30%
  precioClub: 5000,      // $5,000 (rango 2k-8k)
  exitPaquete: 25,       // % no-compradores que toman paquete Orlando
  exitVisa: 8,           // % leads no-agendados que toman visa
};

// ============== SLIDER ==============
const Slider = ({ label, value, min, max, step = 1, onChange, suffix = "%", color = "#c9a227", marks = [], note = "" }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-xs font-bold text-slate-700">{label}</label>
      <span className="text-xl sm:text-2xl font-black tabular-nums" style={{ color }}>
        {value}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full"
      style={{ accentColor: color }}
      data-testid={`slider-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    />
    {marks.length > 0 && (
      <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-0.5">
        {marks.map((m) => <span key={m.v}>{m.lbl}</span>)}
      </div>
    )}
    {note && <p className="text-[11px] text-slate-500 mt-1 italic">{note}</p>}
  </div>
);

const ClubVacacionalSimuladorPage = () => {
  const [scrolled, setScrolled] = useState(false);

  const [vendedores, setVendedores] = useState(DEFAULTS.vendedores);
  const [citasDia, setCitasDia] = useState(DEFAULTS.citasDia);
  const [diasSem, setDiasSem] = useState(DEFAULTS.diasSem);
  const [nivel, setNivel] = useState(DEFAULTS.nivel);
  const [precioClub, setPrecioClub] = useState(DEFAULTS.precioClub);
  const [exitPaquete, setExitPaquete] = useState(DEFAULTS.exitPaquete);
  const [exitVisa, setExitVisa] = useState(DEFAULTS.exitVisa);

  // Financiación
  const [showFinance, setShowFinance] = useState(false);
  const [pctFinancian, setPctFinancian] = useState(DEFAULTS.pctFinancian);
  const [pctInicial, setPctInicial] = useState(DEFAULTS.pctInicial);
  const [cuotas, setCuotas] = useState(DEFAULTS.cuotas);

  useEffect(() => {
    document.title = "Negocio Club Vacacional — Travel Empire / Club Viajemos";
    const onScroll = () => setScrolled(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const reset = () => {
    setVendedores(DEFAULTS.vendedores);
    setCitasDia(DEFAULTS.citasDia);
    setDiasSem(DEFAULTS.diasSem);
    setNivel(DEFAULTS.nivel);
    setPrecioClub(DEFAULTS.precioClub);
    setExitPaquete(DEFAULTS.exitPaquete);
    setExitVisa(DEFAULTS.exitVisa);
    setPctFinancian(DEFAULTS.pctFinancian);
    setPctInicial(DEFAULTS.pctInicial);
    setCuotas(DEFAULTS.cuotas);
  };

  const nivelObj = NIVELES.find((n) => n.key === nivel) || NIVELES[2];
  const cierreClub = nivelObj.pct;

  const calc = useMemo(() => {
    // Operación
    const citasMes = (vendedores * citasDia * diasSem * SEMANAS) / 12;
    const leadsMes = citasMes * LEADS_PER_CITA;
    const costoLeadsMes = leadsMes * LEAD_COST;

    // 1. Club Vacacional (familia = 1 venta)
    const compradoresClubMes = citasMes * (cierreClub / 100);
    const ingresoClubMes = compradoresClubMes * precioClub;
    const costoClubMes = compradoresClubMes * CLUB_COST;
    const ingresoClubNetoMes = ingresoClubMes - costoClubMes;

    // 2. Exit Sale Paquete Orlando (de los que asistieron a cita pero NO compraron club)
    const noCompradoresClub = citasMes - compradoresClubMes;
    const compradoresPaqueteMes = noCompradoresClub * (exitPaquete / 100);
    const ingresoPaqueteMes = compradoresPaqueteMes * PAQUETE_PRICE;

    // 3. Exit Sale Visa (leads que NUNCA agendaron cita)
    const leadsNoAgendados = leadsMes - citasMes;
    const compradoresVisaMes = leadsNoAgendados * (exitVisa / 100);
    const visasVendidasMes = compradoresVisaMes * (1 + FAMILIARES_EXTRA);
    const ingresoVisaMes = visasVendidasMes * VISA_PRICE;

    // Totales
    const ingresoTotalMes = ingresoClubMes + ingresoPaqueteMes + ingresoVisaMes;
    const costoTotalMes = costoLeadsMes + costoClubMes;
    const utilidadMes = ingresoTotalMes - costoTotalMes;
    const utilidadAnual = utilidadMes * 12;
    const margen = ingresoTotalMes > 0 ? utilidadMes / ingresoTotalMes : 0;
    const roi = costoTotalMes > 0 ? utilidadMes / costoTotalMes : 0;

    // ============ FINANCIACIÓN (solo aplica al Club) ============
    // Las exit sales (Paquete $598 / Visa $49) se asumen 100% al contado.
    const fracFinancia = pctFinancian / 100;
    const fracInicial = pctInicial / 100;
    const moraFactor = 1 - MOROSIDAD_PCT / 100; // se aplica solo a las cuotas

    const compradoresFinancian = compradoresClubMes * fracFinancia;
    const compradoresContado = compradoresClubMes - compradoresFinancian;

    const ingresoClubContadoMes = compradoresContado * precioClub;            // 100% pagado al cierre
    const inicialFinanciadosMes = compradoresFinancian * precioClub * fracInicial;
    const saldoFinanciado = precioClub * (1 - fracInicial);                   // saldo por familia
    const cuotaMensual = cuotas > 0 ? saldoFinanciado / cuotas : 0;           // cuota mensual por familia
    const cuotasMesFinanciadas = compradoresFinancian * cuotaMensual * moraFactor;

    // Cash Club Mes 1 (arranque): contado + inicial + 1 cuota del mes
    const cashClubMes1 = ingresoClubContadoMes + inicialFinanciadosMes + cuotasMesFinanciadas;
    // Cash Club régimen estable (mes 13+): cada mes se cobran "cuotas" cuotas (la cola acumulada)
    const cashClubSteady = ingresoClubContadoMes + inicialFinanciadosMes + cuotasMesFinanciadas * cuotas;

    // Total con financiación (Club ajustado + Paquete + Visa al contado)
    const cashTotalMes1 = cashClubMes1 + ingresoPaqueteMes + ingresoVisaMes;
    const cashTotalSteady = cashClubSteady + ingresoPaqueteMes + ingresoVisaMes;

    // Utilidad: el costo del Club ($499) se paga al cierre (regardless of financing)
    const utilidadMes1 = cashTotalMes1 - costoTotalMes;
    const utilidadSteady = cashTotalSteady - costoTotalMes;

    // Cuentas por cobrar en régimen estable (cola pendiente promedio)
    // = compradoresFinancian × saldoFinanciado × (cuotas - 1) / 2 (aproximación promedio)
    const cuentasPorCobrar = compradoresFinancian * saldoFinanciado * Math.max(0, cuotas - 1) / 2 * moraFactor;

    return {
      citasMes, leadsMes, costoLeadsMes, leadsNoAgendados,
      compradoresClubMes, ingresoClubMes, costoClubMes, ingresoClubNetoMes,
      noCompradoresClub, compradoresPaqueteMes, ingresoPaqueteMes,
      compradoresVisaMes, visasVendidasMes, ingresoVisaMes,
      ingresoTotalMes, costoTotalMes, utilidadMes, utilidadAnual, margen, roi,
      // Financiación
      compradoresFinancian, compradoresContado,
      inicialFinanciadosMes, saldoFinanciado, cuotaMensual, cuotasMesFinanciadas,
      cashClubMes1, cashClubSteady, cashTotalMes1, cashTotalSteady,
      utilidadMes1, utilidadSteady, cuentasPorCobrar,
    };
  }, [vendedores, citasDia, diasSem, cierreClub, precioClub, exitPaquete, exitVisa, pctFinancian, pctInicial, cuotas]);

  return (
    <div data-testid="negocio-club-vacacional-page" className="min-h-screen bg-white">
      {/* Header */}
      <header className={`border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-50 transition-shadow ${scrolled ? "shadow-md" : ""}`}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/analisisfinanciero" data-testid="back-financiero" className="flex items-center gap-1 text-slate-500 hover:text-[#1D1D1B] text-sm font-medium">
            <ArrowLeft className="h-4 w-4" /> Análisis Financiero
          </Link>
          <div className="w-px h-5 bg-slate-200" />
          <p className="text-sm font-semibold text-[#1D1D1B] truncate flex-1">Negocio Club Vacacional</p>
          <Link
            to="/analisisfinanciero/certificados"
            data-testid="link-negocio-certificados"
            className="hidden md:flex items-center gap-1.5 bg-[#80C753]/15 hover:bg-[#80C753]/25 text-[#5a9939] text-xs font-black px-3 py-1.5 rounded-lg transition-colors border border-[#80C753]/30"
          >
            Certificados <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to="/analisisfinanciero"
            data-testid="link-compartir"
            className="hidden sm:flex items-center gap-1.5 bg-[#1d4ed8]/15 hover:bg-[#1d4ed8]/25 text-[#1d4ed8] text-xs font-black px-3 py-1.5 rounded-lg transition-colors border border-[#1d4ed8]/30"
          >
            Compartir <ArrowRight className="h-3 w-3" />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* HERO */}
      <section data-testid="club-hero" className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <div className="inline-flex items-center gap-2 bg-[#c9a227]/15 text-[#c9a227] px-3 py-1 rounded-full text-[10px] font-mono tracking-[3px] uppercase mb-5">
            <Crown className="h-3 w-3" /> Producto premium · Travel Empire
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black leading-[0.95] mb-6 max-w-4xl">
            Negocio<br/>
            <span className="text-[#c9a227]">Club Vacacional.</span><br/>
            <span className="text-[#80C753]" data-testid="club-hero-anual">{fmtUSD(Math.max(0, calc.utilidadAnual))}</span><span className="text-[#80C753]"> al año.</span>
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-5 text-sm sm:text-base">
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Club (neto)</span>
              <span className="font-black text-white tabular-nums">{fmtUSD(Math.max(0, calc.ingresoClubNetoMes * 12))}</span>
            </div>
            <span className="text-slate-500">+</span>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Paquete Orlando</span>
              <span className="font-black text-[#80C753] tabular-nums">{fmtUSD(Math.max(0, calc.ingresoPaqueteMes * 12))}</span>
            </div>
            <span className="text-slate-500">+</span>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Visas</span>
              <span className="font-black text-[#c9a227] tabular-nums">{fmtUSD(Math.max(0, calc.ingresoVisaMes * 12))}</span>
            </div>
          </div>
          <p className="text-slate-300 text-lg sm:text-xl max-w-3xl">
            Ticket alto, embudo de tres niveles. Vendes el <strong className="text-white">Club Vacacional ($2k–$8k por familia, ${CLUB_COST} de costo)</strong>; al que no compra le ofreces un <strong className="text-white">paquete a Orlando por ${PAQUETE_PRICE}</strong>; y al lead que ni siquiera agendó cita le vendes la <strong className="text-white">asesoría de visa por ${VISA_PRICE}</strong>. Ningún lead se desperdicia.
          </p>
        </div>
      </section>

      {/* SIMULADOR */}
      <section data-testid="club-simulador" className="bg-slate-50 border-y-4 border-[#c9a227]">
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
          <div className="flex items-start sm:items-center justify-between gap-3 mb-5 flex-col sm:flex-row">
            <div>
              <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-1 flex items-center gap-1.5">
                <Sliders className="h-3 w-3" /> Prueba tus propios números
              </p>
              <h2 className="text-2xl sm:text-4xl font-black text-[#1D1D1B] leading-tight">
                Simulador interactivo
              </h2>
            </div>
            <button
              type="button"
              onClick={reset}
              data-testid="btn-reset-club"
              className="flex items-center gap-1.5 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold px-3 py-2 rounded-lg border border-slate-200 transition-colors"
            >
              <RotateCcw className="h-3 w-3" /> Volver a valores base
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* OPERACIÓN */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-[#c9a227]/40 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="w-8 h-8 rounded-lg bg-[#c9a227]/15 text-[#c9a227] flex items-center justify-center text-sm font-black">★</div>
                <h3 className="font-black text-[#1D1D1B]">Operación</h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#c9a227]/10 text-[#c9a227]">
                  Sala de ventas con citas presenciales (1 cita ≈ {LEADS_PER_CITA} leads)
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Slider
                  label="Vendedores"
                  value={vendedores} min={1} max={50} onChange={setVendedores}
                  color="#c9a227" suffix=""
                  marks={[{ v: 1, lbl: "1" }, { v: 10, lbl: "10" }, { v: 25, lbl: "25" }, { v: 50, lbl: "50" }]}
                  note="Como broker puedes llegar a 50 vendedores."
                />
                <Slider
                  label="Citas por día por vendedor"
                  value={citasDia} min={1} max={6} onChange={setCitasDia}
                  color="#1d4ed8" suffix=""
                  marks={[{ v: 1, lbl: "1" }, { v: 2, lbl: "2 base" }, { v: 4, lbl: "4" }, { v: 6, lbl: "6" }]}
                  note="Citas presenciales (presentaciones del Club) por día."
                />
                <Slider
                  label="Días que trabajas por semana"
                  value={diasSem} min={1} max={7} onChange={setDiasSem}
                  color="#E04A6F" suffix=""
                  marks={[{ v: 1, lbl: "1" }, { v: 3, lbl: "3" }, { v: 6, lbl: "6 base" }, { v: 7, lbl: "7" }]}
                  note="De 1 a 7 días por semana."
                />
              </div>

              <div className="mt-4 bg-slate-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border border-slate-200">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Citas/mes</p>
                  <p className="text-xl font-black text-[#1d4ed8] tabular-nums">{fmtNum(calc.citasMes, 0)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Leads/mes</p>
                  <p className="text-xl font-black text-[#1D1D1B] tabular-nums">{fmtNum(calc.leadsMes, 0)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Costo leads/mes</p>
                  <p className="text-xl font-black text-red-600 tabular-nums">{fmtUSD(calc.costoLeadsMes)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Ventas Club/mes</p>
                  <p className="text-xl font-black text-[#c9a227] tabular-nums">{fmtNum(calc.compradoresClubMes, 1)}</p>
                </div>
              </div>
            </div>

            {/* NIVEL DE CIERRE + PRECIO CLUB */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#c9a227]/15 text-[#c9a227] flex items-center justify-center text-sm font-black">1</div>
                <h3 className="font-black text-[#1D1D1B]">Venta del Club Vacacional</h3>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <label className="text-xs font-bold text-slate-700">Nivel del vendedor</label>
                  <span className="text-xl sm:text-2xl font-black tabular-nums" style={{ color: nivelObj.color }}>
                    {cierreClub}%
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {NIVELES.map((n) => (
                    <button
                      key={n.key}
                      type="button"
                      onClick={() => setNivel(n.key)}
                      data-testid={`nivel-${n.key}`}
                      className={`px-2 py-2 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all border-2 ${
                        nivel === n.key
                          ? "text-white shadow-md"
                          : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                      }`}
                      style={
                        nivel === n.key
                          ? { backgroundColor: n.color, borderColor: n.color }
                          : {}
                      }
                    >
                      {n.label}
                      <span className="block text-[10px] font-mono opacity-80">{n.pct}%</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 italic">
                  Tasa de cierre por nivel del vendedor: Amateur 10% → Elite 40%.
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <Slider
                  label="Precio del Club Vacacional (por familia)"
                  value={precioClub} min={2000} max={8000} step={500} onChange={setPrecioClub}
                  color="#c9a227" suffix=""
                  marks={[{ v: 2000, lbl: "$2k" }, { v: 4000, lbl: "$4k" }, { v: 6000, lbl: "$6k" }, { v: 8000, lbl: "$8k" }]}
                  note="Una venta = una familia. El ticket varía entre $2,000 y $8,000."
                />
              </div>

              <div className="mt-4 bg-[#c9a227]/8 rounded-xl p-4 border border-[#c9a227]/20">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#c9a227] mb-1">Ingreso Club/mes (neto del costo)</p>
                <p className="text-2xl font-black text-[#c9a227] tabular-nums">{fmtUSD(calc.ingresoClubNetoMes)}</p>
                <p className="text-[11px] text-slate-500 mt-1">{fmtNum(calc.compradoresClubMes, 1)} familias × ({fmtUSD(precioClub)} − ${CLUB_COST} costo) = {fmtUSD(calc.ingresoClubNetoMes)}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Bruto {fmtUSD(calc.ingresoClubMes)} − costo Club {fmtUSD(calc.costoClubMes)}</p>
              </div>
            </div>

            {/* EXIT SALES */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#80C753]/15 text-[#80C753] flex items-center justify-center text-sm font-black">2</div>
                <h3 className="font-black text-[#1D1D1B]">Exit Sale · Paquete Orlando ${PAQUETE_PRICE}</h3>
              </div>

              <Slider
                label="% no-compradores que toman el paquete"
                value={exitPaquete} min={5} max={50} step={1} onChange={setExitPaquete}
                color="#80C753"
                marks={[{ v: 5, lbl: "5%" }, { v: 25, lbl: "25% base" }, { v: 35, lbl: "35%" }, { v: 50, lbl: "50%" }]}
                note="A los que asistieron a la cita pero NO compraron el club, se les ofrece un paquete a Orlando por ${PAQUETE_PRICE}."
              />

              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#1d4ed8]/15 text-[#1d4ed8] flex items-center justify-center text-sm font-black">3</div>
                  <h3 className="font-black text-[#1D1D1B]">Exit Sale · Visa ${VISA_PRICE}</h3>
                </div>
                <Slider
                  label="% leads no-agendados que toman la visa"
                  value={exitVisa} min={2} max={20} step={1} onChange={setExitVisa}
                  color="#1d4ed8"
                  marks={[{ v: 2, lbl: "2%" }, { v: 8, lbl: "8% base" }, { v: 14, lbl: "14%" }, { v: 20, lbl: "20%" }]}
                  note={`A los leads que nunca agendaron cita se les vende la asesoría de visa a $${VISA_PRICE} por persona (titular + 2 familiares).`}
                />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="bg-[#80C753]/8 rounded-xl p-3 border border-[#80C753]/20">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-[#80C753] mb-1 flex items-center gap-1">
                    <Plane className="h-3 w-3" /> Paquetes/mes
                  </p>
                  <p className="text-lg font-black text-[#80C753] tabular-nums">{fmtUSD(calc.ingresoPaqueteMes)}</p>
                  <p className="text-[10px] text-slate-500">{fmtNum(calc.compradoresPaqueteMes, 1)} ventas</p>
                </div>
                <div className="bg-[#1d4ed8]/8 rounded-xl p-3 border border-[#1d4ed8]/20">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-[#1d4ed8] mb-1 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Visas/mes
                  </p>
                  <p className="text-lg font-black text-[#1d4ed8] tabular-nums">{fmtUSD(calc.ingresoVisaMes)}</p>
                  <p className="text-[10px] text-slate-500">{fmtNum(calc.visasVendidasMes, 0)} visas</p>
                </div>
              </div>
            </div>
          </div>

          {/* RESULTADOS LIVE */}
          <div className="mt-5 bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white rounded-2xl p-6 sm:p-8 border border-[#c9a227]/30">
            <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Resultados en vivo</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Ingreso bruto/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-white tabular-nums">{fmtUSD(Math.max(0, calc.ingresoTotalMes))}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Costos totales</p>
                <p className="text-2xl sm:text-3xl font-black text-red-400 tabular-nums">−{fmtUSD(calc.costoTotalMes)}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">Marketing {fmtUSD(calc.costoLeadsMes)} + Club {fmtUSD(calc.costoClubMes)}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Utilidad/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums" data-testid="util-mes-club">{fmtUSD(Math.max(0, calc.utilidadMes))}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Utilidad/año</p>
                <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums" data-testid="util-anual-club">{fmtUSD(Math.max(0, calc.utilidadAnual))}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-4 text-xs sm:text-sm">
              <div>
                <span className="text-slate-400">Margen sobre ingreso: </span>
                <strong className="text-white">{(calc.margen * 100).toFixed(1)}%</strong>
              </div>
              <div>
                <span className="text-slate-400">ROI sobre costos: </span>
                <strong className="text-[#80C753]">{calc.roi.toFixed(1)}x</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINANCIACIÓN (colapsable) */}
      <section data-testid="club-financiacion" className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          <button
            type="button"
            onClick={() => setShowFinance((v) => !v)}
            data-testid="toggle-financiacion"
            className="w-full flex items-center justify-between gap-3 group text-left hover:opacity-80 transition-opacity"
            aria-expanded={showFinance}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-[#c9a227]/10 text-[#c9a227] flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-0.5">Modelo realista · opcional</p>
                <h3 className="text-lg sm:text-2xl font-black text-[#1D1D1B] truncate">
                  Flujo de caja con financiación
                </h3>
                <p className="text-xs text-slate-500 hidden sm:block mt-0.5">
                  Asume que un % de tus clientes pagan a cuotas con {MOROSIDAD_PCT}% de morosidad
                </p>
              </div>
            </div>
            <ChevronDown className={`h-6 w-6 text-slate-400 transition-transform flex-shrink-0 ${showFinance ? "rotate-180" : ""}`} />
          </button>

          {showFinance && (
            <div data-testid="financiacion-content" className="mt-6 space-y-5">
              {/* SLIDERS */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 bg-slate-50 rounded-2xl p-5 border border-slate-200">
                <Slider
                  label="% de clientes que financian"
                  value={pctFinancian} min={0} max={100} step={5} onChange={setPctFinancian}
                  color="#c9a227"
                  marks={[{ v: 0, lbl: "0%" }, { v: 40, lbl: "40% base" }, { v: 70, lbl: "70%" }, { v: 100, lbl: "100%" }]}
                  note="Porcentaje de las ventas del Club que se pagan a cuotas en lugar de al contado."
                />
                <Slider
                  label="% de inicial (down payment)"
                  value={pctInicial} min={10} max={50} step={5} onChange={setPctInicial}
                  color="#1d4ed8"
                  marks={[{ v: 10, lbl: "10% mín." }, { v: 25, lbl: "25%" }, { v: 35, lbl: "35%" }, { v: 50, lbl: "50%" }]}
                  note="Pago inicial que entrega el cliente al cierre. El saldo se reparte en cuotas."
                />
                <Slider
                  label="Cuotas (meses)"
                  value={cuotas} min={6} max={36} step={1} onChange={setCuotas}
                  color="#E04A6F" suffix=""
                  marks={[{ v: 6, lbl: "6" }, { v: 12, lbl: "12 base" }, { v: 24, lbl: "24" }, { v: 36, lbl: "36" }]}
                  note={`Cuota mensual estimada: ${fmtUSD(calc.cuotaMensual, 0)} por familia financiada.`}
                />
              </div>

              {/* RESUMEN COMPRADORES */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Contado/mes</p>
                  <p className="text-lg font-black text-[#80C753] tabular-nums">{fmtNum(calc.compradoresContado, 1)}</p>
                  <p className="text-[10px] text-slate-400">familias pagan full</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">A cuotas/mes</p>
                  <p className="text-lg font-black text-[#c9a227] tabular-nums">{fmtNum(calc.compradoresFinancian, 1)}</p>
                  <p className="text-[10px] text-slate-400">familias financian</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Cuota por familia</p>
                  <p className="text-lg font-black text-[#1d4ed8] tabular-nums">{fmtUSD(calc.cuotaMensual, 0)}</p>
                  <p className="text-[10px] text-slate-400">durante {cuotas} meses</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-slate-200">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Morosidad</p>
                  <p className="text-lg font-black text-red-500 tabular-nums">{MOROSIDAD_PCT}%</p>
                  <p className="text-[10px] text-slate-400">sobre las cuotas</p>
                </div>
              </div>

              {/* COMPARATIVA MES 1 vs RÉGIMEN ESTABLE */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* MES 1 */}
                <div data-testid="cash-mes-1" className="bg-white rounded-2xl p-6 border-2 border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-mono tracking-[3px] uppercase text-slate-500">Mes 1 · arranque</p>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Cash real</span>
                  </div>
                  <p className="text-3xl sm:text-4xl font-black text-[#1D1D1B] tabular-nums">{fmtUSD(calc.cashTotalMes1)}</p>
                  <p className="text-xs text-slate-500 mt-1">Cash entrante el primer mes</p>
                  <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-600 space-y-1.5">
                    <div className="flex justify-between"><span>Contado Club</span><span className="font-bold tabular-nums">{fmtUSD(calc.compradoresContado * precioClub)}</span></div>
                    <div className="flex justify-between"><span>Inicial {pctInicial}% financiados</span><span className="font-bold tabular-nums">{fmtUSD(calc.inicialFinanciadosMes)}</span></div>
                    <div className="flex justify-between"><span>1 cuota financiados</span><span className="font-bold tabular-nums">{fmtUSD(calc.cuotasMesFinanciadas)}</span></div>
                    <div className="flex justify-between"><span>Paquete + Visas (contado)</span><span className="font-bold tabular-nums">{fmtUSD(calc.ingresoPaqueteMes + calc.ingresoVisaMes)}</span></div>
                    <div className="flex justify-between pt-1 border-t border-slate-100 text-slate-400"><span>− Costos (marketing + Club)</span><span className="font-bold tabular-nums text-red-500">−{fmtUSD(calc.costoTotalMes)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-slate-200 text-[#1D1D1B]">
                      <span className="font-bold">Utilidad mes 1</span>
                      <span className="font-black text-base tabular-nums">{fmtUSD(calc.utilidadMes1)}</span>
                    </div>
                  </div>
                </div>

                {/* RÉGIMEN ESTABLE */}
                <div data-testid="cash-steady" className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white rounded-2xl p-6 border-2 border-[#c9a227]">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227]">Mes 13+ · régimen estable</p>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#c9a227]/20 text-[#c9a227]">Cash recurrente</span>
                  </div>
                  <p className="text-3xl sm:text-4xl font-black text-white tabular-nums">{fmtUSD(calc.cashTotalSteady)}</p>
                  <p className="text-xs text-slate-400 mt-1">Cash mensual una vez la cola de cuotas se acumula</p>
                  <div className="mt-4 pt-4 border-t border-white/10 text-xs text-slate-300 space-y-1.5">
                    <div className="flex justify-between"><span>Contado Club</span><span className="font-bold tabular-nums">{fmtUSD(calc.compradoresContado * precioClub)}</span></div>
                    <div className="flex justify-between"><span>Inicial nuevos financiados</span><span className="font-bold tabular-nums">{fmtUSD(calc.inicialFinanciadosMes)}</span></div>
                    <div className="flex justify-between"><span>{cuotas} cuotas activas en cola</span><span className="font-bold tabular-nums text-[#c9a227]">{fmtUSD(calc.cuotasMesFinanciadas * cuotas)}</span></div>
                    <div className="flex justify-between"><span>Paquete + Visas (contado)</span><span className="font-bold tabular-nums">{fmtUSD(calc.ingresoPaqueteMes + calc.ingresoVisaMes)}</span></div>
                    <div className="flex justify-between pt-1 border-t border-white/10 text-slate-400"><span>− Costos (marketing + Club)</span><span className="font-bold tabular-nums text-red-400">−{fmtUSD(calc.costoTotalMes)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-white/20 text-white">
                      <span className="font-bold">Utilidad estable</span>
                      <span className="font-black text-base tabular-nums text-[#80C753]">{fmtUSD(calc.utilidadSteady)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* CARTERA POR COBRAR */}
              <div className="bg-[#c9a227]/8 rounded-2xl p-5 border border-[#c9a227]/30 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-[#c9a227] flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-xs sm:text-sm text-slate-700">
                  <p className="font-bold text-[#1D1D1B] mb-1">
                    Cartera por cobrar acumulada (régimen estable): <span className="text-[#c9a227] tabular-nums">{fmtUSD(calc.cuentasPorCobrar)}</span>
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    Es el saldo promedio que tienes pendiente de cobrar de todas las familias que están pagando a cuotas en este momento. No es ingreso perdido — es dinero ya vendido que entra mes a mes. La morosidad del {MOROSIDAD_PCT}% ya está descontada en los números de arriba.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FUNNEL */}
      <section data-testid="club-funnel" className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">El embudo del Club Vacacional</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-3">
            Cada lead termina en una de cuatro rutas
          </h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-3xl mb-10">
            Compra el club, compra el paquete, compra la visa o no compra. Tres de las cuatro rutas son monetizables — todas se calculan en vivo abajo.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-slate-50 rounded-2xl p-5 border-2 border-slate-200">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-slate-500 mb-1">Paso 1</p>
              <p className="text-xs text-slate-600 mb-3">Leads totales del mes</p>
              <p className="text-2xl sm:text-3xl font-black text-[#1D1D1B] tabular-nums">{fmtNum(calc.leadsMes, 0)}</p>
              <p className="text-xs text-slate-500 mt-2">a ${LEAD_COST} c/u = <strong className="text-red-600">{fmtUSD(calc.costoLeadsMes)}</strong></p>
            </div>

            <div className="bg-[#c9a227]/10 rounded-2xl p-5 border-2 border-[#c9a227]/30">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-1">Paso 2 · {cierreClub}%</p>
              <p className="text-xs text-slate-600 mb-3">Compran el Club Vacacional</p>
              <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums">{fmtNum(calc.compradoresClubMes, 1)}</p>
              <p className="text-xs text-slate-500 mt-2">{fmtNum(calc.compradoresClubMes, 1)} familias × {fmtUSD(precioClub)}</p>
            </div>

            <div className="bg-[#80C753]/10 rounded-2xl p-5 border-2 border-[#80C753]/30">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#80C753] mb-1">Paso 3 · {exitPaquete}%</p>
              <p className="text-xs text-slate-600 mb-3">No compraron club → toman paquete Orlando</p>
              <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums">{fmtNum(calc.compradoresPaqueteMes, 1)}</p>
              <p className="text-xs text-slate-500 mt-2">{fmtNum(calc.compradoresPaqueteMes, 1)} × ${PAQUETE_PRICE}</p>
            </div>

            <div className="bg-[#1d4ed8]/10 rounded-2xl p-5 border-2 border-[#1d4ed8]/30">
              <p className="text-[10px] font-mono tracking-[3px] uppercase text-[#1d4ed8] mb-1">Paso 4 · {exitVisa}%</p>
              <p className="text-xs text-slate-600 mb-3">Nunca agendaron cita → compran visa</p>
              <p className="text-2xl sm:text-3xl font-black text-[#1d4ed8] tabular-nums">{fmtNum(calc.compradoresVisaMes, 0)}</p>
              <p className="text-xs text-slate-500 mt-2">{fmtNum(calc.visasVendidasMes, 0)} visas × ${VISA_PRICE}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white rounded-2xl p-6 sm:p-8 border border-[#c9a227]/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Club neto/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#c9a227] tabular-nums">{fmtUSD(calc.ingresoClubNetoMes)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Bruto {fmtUSD(calc.ingresoClubMes)} − ${CLUB_COST} costo c/u</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Ingreso Paquete/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#80C753] tabular-nums">{fmtUSD(calc.ingresoPaqueteMes)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Exit sale a no-compradores del club</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Ingreso Visas/mes</p>
                <p className="text-2xl sm:text-3xl font-black text-[#1d4ed8] tabular-nums">{fmtUSD(calc.ingresoVisaMes)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Recuperación de leads no-agendados</p>
              </div>
            </div>
            <div className="border-t border-white/10 pt-5">
              <p className="text-sm text-slate-300 leading-relaxed">
                <strong className="text-white">Total bruto: {fmtUSD(calc.ingresoTotalMes)}/mes</strong> menos <strong className="text-red-400">{fmtUSD(calc.costoTotalMes)}</strong> de costos (marketing {fmtUSD(calc.costoLeadsMes)} + costo Club {fmtUSD(calc.costoClubMes)})
                = <strong className="text-[#80C753]">{fmtUSD(calc.utilidadMes)}/mes limpios</strong> (
                {fmtUSD(calc.utilidadAnual)}/año). El embudo de tres pasos garantiza que <strong className="text-white">ningún lead se desperdicie</strong>: si no compra el club, compra el paquete; si nunca agendó, compra la visa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NIVELES */}
      <section data-testid="club-niveles" className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Tu nivel define tu cheque</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-8">
            De Amateur a Elite
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {NIVELES.map((n) => (
              <button
                key={n.key}
                type="button"
                onClick={() => setNivel(n.key)}
                data-testid={`nivel-card-${n.key}`}
                className={`text-left rounded-2xl p-5 border-2 transition-all hover:scale-[1.02] ${
                  nivel === n.key ? "shadow-lg" : "bg-white border-slate-200"
                }`}
                style={
                  nivel === n.key
                    ? { backgroundColor: `${n.color}15`, borderColor: n.color }
                    : {}
                }
              >
                <p className="text-[10px] font-mono tracking-[3px] uppercase mb-1" style={{ color: n.color }}>
                  Nivel
                </p>
                <p className="text-xl font-black text-[#1D1D1B] mb-2">{n.label}</p>
                <p className="text-3xl sm:text-4xl font-black tabular-nums" style={{ color: n.color }}>
                  {n.pct}%
                </p>
                <p className="text-[11px] text-slate-500 mt-1">tasa de cierre</p>
              </button>
            ))}
          </div>

          <div className="mt-6 bg-white rounded-xl p-4 border border-slate-200 flex items-start gap-2 text-xs text-slate-600">
            <AlertCircle className="h-4 w-4 text-[#c9a227] flex-shrink-0 mt-0.5" />
            <p>
              Estos rangos reflejan la curva real de un vendedor de Club Vacacional: <strong>Amateur</strong> apenas inicia, <strong>Calentando</strong> ya sigue el guion, <strong>Pro</strong> domina objeciones, y <strong>Elite</strong> es el top del equipo. Mover este selector recalcula automáticamente todos los ingresos.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="club-cta" className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 text-center">
          <p className="text-[11px] font-mono tracking-[3px] uppercase text-[#c9a227] mb-3">Tu siguiente paso</p>
          <h2 className="text-3xl sm:text-5xl font-black text-[#1D1D1B] leading-tight mb-4">
            ¿Quieres operar el Club Vacacional?
          </h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            El Manual completo te enseña a montar la sala de ventas, captar leads a ${LEAD_COST}, agendar citas y cerrar familias en el ticket de $5,000+.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Link
              to="/manual27"
              data-testid="club-cta-manual27"
              className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#c9a227] to-[#e0b835] hover:from-[#e0b835] hover:to-[#c9a227] text-[#0a0a14] font-black text-base px-8 py-4 rounded-xl uppercase tracking-wider transition-all hover:scale-[1.03] shadow-lg shadow-[#c9a227]/30"
            >
              <Lock className="h-4 w-4" /> Acceder por $27 USD
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/analisisfinanciero"
              data-testid="club-cta-volver"
              className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-[#1D1D1B] font-bold text-sm px-6 py-4 rounded-xl uppercase tracking-wider transition-colors border border-slate-200"
            >
              <ArrowLeft className="h-4 w-4" /> Ver Análisis Financiero principal
            </Link>
          </div>

          <p className="text-xs text-slate-400">
            <span className="line-through">$297</span> · Hoy $27 USD · Acceso inmediato
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-xs text-slate-400">
          Tons of Leads LLC · Florida Seller of Travel ST39568 · DBA Viajemos · Negocio Club Vacacional — Edición 2026.
          <br />
          Cifras proyectadas. Resultados reales pueden variar.
        </div>
      </footer>
      <WhatsAppFAB context="club-vacacional" />
    </div>
  );
};

export default ClubVacacionalSimuladorPage;
